import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from './supabaseClient';
import { 
  ArrowLeft, 
  BookmarkX, 
  ArrowUpRight, 
  Briefcase, 
  Clock,
  Trash2
} from 'lucide-react';

const SavedPage = () => {
  const [savedIdeas, setSavedIdeas] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSavedContent();
  }, []);

  const fetchSavedContent = async () => {
    const ids = JSON.parse(localStorage.getItem('savedIdeas')) || [];
    
    if (ids.length === 0) {
      setSavedIdeas([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    // Mengambil data dari Supabase yang ID-nya ada di dalam list tersimpan
    const { data, error } = await supabase
      .from('ideas')
      .select('*')
      .in('id', ids);

    if (!error) {
      setSavedIdeas(data);
    }
    setLoading(false);
  };

  const removeSaved = (id) => {
    const currentIds = JSON.parse(localStorage.getItem('savedIdeas')) || [];
    const updatedIds = currentIds.filter(savedId => savedId !== id);
    localStorage.setItem('savedIdeas', JSON.stringify(updatedIds));
    
    // Update state lokal agar UI langsung berubah
    setSavedIdeas(savedIdeas.filter(item => item.id !== id));
  };

  const clearAll = () => {
    if (window.confirm("Hapus semua ide yang tersimpan?")) {
      localStorage.removeItem('savedIdeas');
      setSavedIdeas([]);
    }
  };

  const formatRp = (v) => new Intl.NumberFormat('id-ID', { 
    style: 'currency', currency: 'IDR', maximumFractionDigits: 0 
  }).format(v);

  return (
    <div className="bg-[#F8FAFC] min-h-screen font-['Onest']">
      {/* Header */}
      <nav className="bg-white border-b border-slate-100 sticky top-0 z-50">
        <div className="container mx-auto px-6 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-slate-600 font-bold hover:text-emerald-600 transition-colors">
            <ArrowLeft size={20} /> Kembali
          </Link>
          <h1 className="text-xl font-black text-slate-900">Ide Tersimpan</h1>
          {savedIdeas.length > 0 ? (
            <button onClick={clearAll} className="text-red-500 hover:bg-red-50 p-2 rounded-xl transition-all">
              <Trash2 size={20} />
            </button>
          ) : <div className="w-10"></div>}
        </div>
      </nav>

      <main className="container mx-auto px-6 py-12">
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-emerald-600"></div>
          </div>
        ) : savedIdeas.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
            {savedIdeas.map((item) => (
              <div key={item.id} className="relative group bg-white rounded-[2.5rem] border border-slate-100 overflow-hidden flex flex-col transition-all hover:shadow-xl">
                <Link to={`/ide/${item.slug}`} className="block h-56 overflow-hidden">
                  <img src={item.img_url} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" alt={item.judul} />
                </Link>
                
                <button 
                  onClick={() => removeSaved(item.id)}
                  className="absolute top-4 right-4 p-3 bg-white/90 backdrop-blur-md text-red-500 rounded-2xl shadow-lg hover:bg-red-500 hover:text-white transition-all"
                  title="Hapus dari simpanan"
                >
                  <BookmarkX size={18} />
                </button>

                <div className="p-8 flex flex-col flex-grow">
                  <div className="flex gap-2 mb-4">
                    <span className="text-[10px] font-black uppercase tracking-widest bg-emerald-50 text-emerald-600 px-3 py-1 rounded-lg">
                      {item.tag}
                    </span>
                  </div>
                  
                  <h4 className="text-lg font-bold text-slate-900 mb-6 leading-snug line-clamp-2">
                    {item.judul}
                  </h4>

                  <div className="mt-auto pt-6 border-t border-slate-50 flex justify-between items-center">
                    <div>
                      <p className="text-[9px] font-bold text-slate-400 uppercase mb-1">Estimasi Modal</p>
                      <p className="text-lg font-black text-slate-900">{formatRp(item.modal_min)}</p>
                    </div>
                    <Link to={`/ide/${item.slug}`} className="w-10 h-10 rounded-xl bg-slate-900 flex items-center justify-center text-white hover:bg-emerald-600 transition-all">
                      <ArrowUpRight size={18} />
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-32 bg-white rounded-[3rem] border-2 border-dashed border-slate-100 max-w-2xl mx-auto">
            <div className="w-20 h-20 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-6">
              <BookmarkX className="text-slate-300" size={32} />
            </div>
            <h2 className="text-2xl font-black text-slate-900 mb-2">Belum ada ide tersimpan</h2>
            <p className="text-slate-400 mb-8">Klik ikon bookmark pada ide yang Anda sukai untuk <br/> menyimpannya di sini.</p>
            <Link to="/" className="inline-flex items-center gap-2 bg-emerald-600 text-white px-8 py-4 rounded-2xl font-bold hover:bg-emerald-700 transition-all shadow-lg shadow-emerald-200">
              Jelajahi Ide Bisnis
            </Link>
          </div>
        )}
      </main>
    </div>
  );
};

export default SavedPage;